import { useEffect, useState } from "react";
import { BookOpen, Download, LoaderCircle, Lock, AlertTriangle } from "lucide-react";
import { useBook, type BookGateReason } from "@/hooks/useBook";
import { apiFetch } from "@/lib/api";

const ROTATING_MESSAGES = [
  "Le Conseil rassemble les données réelles…",
  "Benchmark concurrentiel sourcé…",
  "Signaux de tendance…",
  "Assemblage du dossier…",
];

const INK = "var(--foreground)";
const PAPER = "var(--background)";
const GOLD = "var(--accent-cramoisi)";
const GOLD_LIGHT = "var(--gold-light)";
const GOLD_SOFT = "color-mix(in srgb, var(--accent-cramoisi) 28%, transparent)";
const SURFACE = "rgba(255,23,68,0.05)";
const MUTED = "var(--qv3-grey-500, #8a8580)";

const TEASER_COVER = "/teaser/book-teaser-cover.png";
const TEASER_BENCHMARK = "/teaser/book-teaser-benchmark.png";

export interface BookPanelProps {
  conversationId: string;
}

export function BookPanel({ conversationId }: BookPanelProps) {
  const { state, generate, startCheckout } = useBook(conversationId);
  const [msgIndex, setMsgIndex] = useState(0);
  const [subQuota, setSubQuota] = useState<{ used: number; limit: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ books_used?: number; books_limit?: number }>(`/api/subscription`, { method: "GET" })
      .then((r) => {
        if (cancelled) return;
        if (typeof r.books_used === "number" && typeof r.books_limit === "number") {
          setSubQuota({ used: r.books_used, limit: r.books_limit });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state.status]);

  const booksUsed = state.booksUsed ?? subQuota?.used ?? null;
  const booksLimit = state.booksLimit ?? subQuota?.limit ?? null;

  useEffect(() => {
    if (state.status !== "generating") return;
    const t = setInterval(() => {
      setMsgIndex((i) => (i + 1) % ROTATING_MESSAGES.length);
    }, 4000);
    return () => clearInterval(t);
  }, [state.status]);

  const shellStyle: React.CSSProperties = {
    marginTop: 48,
    padding: "32px 28px",
    border: `1px solid ${GOLD_SOFT}`,
    background: SURFACE,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  };

  const btnPrimary: React.CSSProperties = {
    background: INK,
    color: PAPER,
    border: `1px solid ${GOLD}`,
    padding: "12px 20px",
    fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    textDecoration: "none",
  };

  const btnGhost: React.CSSProperties = {
    background: "transparent",
    color: INK,
    border: `1px solid ${GOLD_SOFT}`,
    padding: "12px 20px",
    fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    textDecoration: "none",
  };

  return (
    <section aria-label="Le Book — dossier de marché" style={shellStyle}>
      <style>{`@keyframes book-spin{to{transform:rotate(360deg)}}`}</style>

      <header style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span
          style={{
            fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
            fontSize: 10,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: GOLD,
          }}
        >
          Le Book
        </span>
        <h2
          style={{
            fontFamily: "var(--qv3-font-serif, Georgia, serif)",
            fontWeight: 400,
            fontSize: 26,
            margin: 0,
            color: INK,
            letterSpacing: "-0.01em",
          }}
        >
          Votre dossier de marché sourcé
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: "var(--qv3-font-serif, Georgia, serif)",
            fontStyle: "italic",
            fontSize: 15,
            lineHeight: 1.55,
            color: MUTED,
          }}
        >
          Benchmark concurrentiel, signaux de tendance et données réelles, assemblés
          en un PDF prêt à partager.
        </p>
      </header>

      {state.status === "ready" && state.exportUrl ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <button
            type="button"
            onClick={() => window.open(state.exportUrl!, "_blank", "noopener,noreferrer")}
            style={btnPrimary}
          >
            <BookOpen size={14} /> Ouvrir le book
          </button>
          <a
            href={state.exportUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            style={btnGhost}
          >
            <Download size={14} /> Télécharger
          </a>
        </div>
      ) : state.status === "generating" || state.status === "queued" || state.status === "polling" ? (
        <div
          role="status"
          aria-live="polite"
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: GOLD,
            }}
          >
            <LoaderCircle
              size={14}
              style={{ animation: "book-spin 1s linear infinite" }}
            />
            Génération en cours · ~2-3 min
          </div>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--qv3-font-serif, Georgia, serif)",
              fontStyle: "italic",
              fontSize: 16,
              color: INK,
            }}
          >
            {ROTATING_MESSAGES[msgIndex]}
          </p>
        </div>
      ) : state.status === "failed" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div
            role="alert"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
              fontSize: 11,
              letterSpacing: 2,
              color: "#B44848",
            }}
          >
            <AlertTriangle size={14} />
            {state.error ?? "Le book n'a pas pu être généré."}
          </div>
          <button type="button" onClick={() => void generate()} style={btnPrimary}>
            Réessayer
          </button>
        </div>
      ) : state.gateReason === "quota_exhausted" ? (
        <QuotaExhausted
          booksUsed={booksUsed}
          booksLimit={booksLimit}
          nextReset={state.nextReset}
        />
      ) : state.gateReason === "needs_purchase" || state.gateReason === "needs_pro" ? (
        <TeaserGate
          reason={state.gateReason}
          onBuy={() => void startCheckout()}
        />
      ) : (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <button type="button" onClick={() => void generate()} style={btnPrimary}>
            <BookOpen size={14} /> Générer votre book
          </button>
          {booksLimit !== null && booksUsed !== null ? (
            <span
              style={{
                fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
                fontSize: 10,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: MUTED,
              }}
            >
              {Math.max(0, booksLimit - booksUsed)}/{booksLimit} books restants
            </span>
          ) : null}
        </div>
      )}
    </section>
  );
}

function QuotaExhausted({
  booksUsed,
  booksLimit,
  nextReset,
}: {
  booksUsed: number | null;
  booksLimit: number | null;
  nextReset: string | null;
}) {
  const used = booksUsed ?? 0;
  const limit = booksLimit ?? 0;
  const dateStr = nextReset
    ? new Date(nextReset).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  return (
    <div
      role="status"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "20px 22px",
        border: `1px solid ${GOLD_SOFT}`,
        background: "rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
          fontSize: 10,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: GOLD,
        }}
      >
        Quota atteint
      </div>
      <p
        style={{
          margin: 0,
          fontFamily: "var(--qv3-font-serif, Georgia, serif)",
          fontSize: 15,
          color: INK,
        }}
      >
        Vous avez utilisé vos {limit} books ({used}/{limit}).
      </p>
      {dateStr ? (
        <p
          style={{
            margin: 0,
            fontFamily: "var(--qv3-font-serif, Georgia, serif)",
            fontStyle: "italic",
            fontSize: 14,
            color: MUTED,
          }}
        >
          Prochain book le {dateStr}.
        </p>
      ) : null}
    </div>
  );
}

function TeaserGate({
  reason,
  onBuy,
}: {
  reason: NonNullable<BookGateReason>;
  onBuy: () => void;
}) {
  const isPro = reason === "needs_pro";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        aria-hidden
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        <TeaserImage src={TEASER_COVER} alt="" />
        <TeaserImage src={TEASER_BENCHMARK} alt="" />
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
            pointerEvents: "none",
          }}
        >
          <Lock size={28} color={GOLD_LIGHT} />
        </div>
      </div>

      <p
        style={{
          margin: 0,
          fontFamily: "var(--qv3-font-serif, Georgia, serif)",
          fontStyle: "italic",
          fontSize: 15,
          color: "var(--qv3-grey-400, #b8b3ac)",
        }}
      >
        {isPro
          ? "Le Book est inclus dans l'abonnement Pro : benchmarks sourcés, signaux de tendance, dossier PDF prêt à partager."
          : "Débloquez le dossier de marché complet : benchmark concurrentiel, signaux de tendance, sources vérifiables."}
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {isPro ? (
          <a
            href="/pricing"
            style={{
              background: INK,
              color: PAPER,
              border: `1px solid ${GOLD}`,
              padding: "12px 20px",
              fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Lock size={14} /> Passer à Pro
          </a>
        ) : (
          <button
            type="button"
            onClick={onBuy}
            style={{
              background: INK,
              color: PAPER,
              border: `1px solid ${GOLD}`,
              padding: "12px 20px",
              fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Lock size={14} /> Débloquer votre book — 49 €
          </button>
        )}
      </div>
    </div>
  );
}

function TeaserImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${GOLD_SOFT}`,
        aspectRatio: "16 / 9",
        background: "rgba(0,0,0,0.4)",
      }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          filter: "blur(2px)",
          transform: "scale(1.06)",
        }}
      />
    </div>
  );
}

export default BookPanel;
